import parleyHttpClient from './parleyHttpClient';
import {
  Account,
  PaginatedResponse,
  CreateAccountRequest,
  CreateAccountResponse,
  UpdateAccountRequest,
  DeactivateAccountResponse,
  CreateKeyResponse,
  KeyExpiryChoice,
  UsageResponse,
  UsageQueryParams,
  UsageExportParams,
  BulkCreateAccountRequest,
  BulkCreateAccountsResponse,
  BulkOperationStatusResponse,
  BulkOperationResultsResponse,
  HealthResponse,
  AdminDashboardStats,
  PerKeyUsageBreakdown,
  StripePaymentMethod,
  AccountTransactionsResponse,
  PostTransactionResponse,
  CostObjectApprovalRecord,
  AffiliationUpdateResult,
  CostObjectApprovalDetail,
  AccountBudgetLimitDetails,
  DormantKeyNotification,
  CliInstallStatsParams,
  CliInstallStatsResponse,
  AccountCliUsageDetail,
  ChatConfig,
  PlatformLlmConfigView,
  PlatformLlmConfigInput,
  RagSourceTypeConfig,
} from '@/types/parley';
import { AxiosResponse } from 'axios';

// ── Accounts ──

// ── Cost-object approval requests ──

export type CostObjectRequestAction = 'approve' | 'deny';

export interface ReviewCostObjectRequestBody {
  action: CostObjectRequestAction;
  denialReason?: string;
}

export async function reviewCostObjectRequest(
  accountId: string,
  costObject: string,
  body: ReviewCostObjectRequestBody
): Promise<void> {
  if (!accountId || !costObject) {
    throw new Error('accountId and costObject are required');
  }
  await parleyHttpClient.patch(
    `/v1/accounts/${accountId}/cost-object-requests/${costObject}`,
    body
  );
}

// ── Accounts ──

export async function listAccounts(params?: {
  page?: number;
  limit?: number;
  status?: string;
  ownerType?: string;
  fundingSource?: string;
  budgetLimit?: 'limited' | 'unlimited';
  createdFrom?: string;
  createdTo?: string;
  billingMode?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<PaginatedResponse<Account>> {
  const { data } = await parleyHttpClient.get<PaginatedResponse<Account>>('/v1/accounts', {
    params,
  });
  return data;
}

export async function listMyAccounts(): Promise<PaginatedResponse<Account>> {
  const { data } = await parleyHttpClient.get<PaginatedResponse<Account>>('/v1/accounts', {
    params: { scope: 'mine', limit: 200 },
  });
  return data;
}

export async function getAccount(accountId: string): Promise<Account> {
  const { data } = await parleyHttpClient.get<Account>(`/v1/accounts/${accountId}`);
  return data;
}

// Owner-scoped fetch. Returns apiKeys[] with rawApiKey populated so the caller
// can reveal/copy their own keys. Backend routes this through
// getAccountByIdForUser regardless of caller role.
export async function getMyAccount(accountId: string): Promise<Account> {
  const { data } = await parleyHttpClient.get<Account>(`/v1/accounts/${accountId}`, {
    params: { scope: 'mine' },
  });
  return data;
}

export async function createAccount(request: CreateAccountRequest): Promise<CreateAccountResponse> {
  const { data } = await parleyHttpClient.post<CreateAccountResponse>('/v1/accounts', request);
  return data;
}

export async function updateAccount(
  accountId: string,
  request: UpdateAccountRequest
): Promise<Account> {
  const { data } = await parleyHttpClient.patch<Account>(`/v1/accounts/${accountId}`, request);
  return data;
}

// Affiliation lives on its own privileged route rather than the general PATCH:
// it decides the allocation pool and the first-account credit, so the API keeps
// it out of reach of account owners. The response carries a consequences report
// alongside the updated account.
export async function updateAccountAffiliation(
  accountId: string,
  accountType: string
): Promise<AffiliationUpdateResult> {
  const { data } = await parleyHttpClient.patch<AffiliationUpdateResult>(
    `/v1/accounts/${accountId}/affiliation`,
    { accountType }
  );
  return data;
}

export async function resetAccountFreeModelTokens(accountId: string): Promise<Account> {
  const { data } = await parleyHttpClient.patch<Account>(
    `/v1/accounts/${accountId}/resetFreeModelTokens`
  );
  return data;
}

export async function addAccountAdmin(accountId: string, krbName: string): Promise<Account> {
  const { data } = await parleyHttpClient.post<Account>(`/v1/accounts/${accountId}/admins`, {
    krbName,
  });
  return data;
}

export async function removeAccountAdmin(accountId: string, krbName: string): Promise<Account> {
  const { data } = await parleyHttpClient.delete<Account>(
    `/v1/accounts/${accountId}/admins/${encodeURIComponent(krbName)}`
  );
  return data;
}

export async function deactivateAccount(
  accountId: string,
  reason: string
): Promise<DeactivateAccountResponse> {
  const { data } = await parleyHttpClient.patch<DeactivateAccountResponse>(
    `/v1/accounts/${accountId}/deactivate`,
    { reason }
  );
  return data;
}

export async function reactivateAccount(accountId: string): Promise<void> {
  await parleyHttpClient.patch(`/v1/accounts/${accountId}/reactivate`);
}

// ── Zero content retention (parley-api ADR-039) ──
// Super-admin only. While on, the gateway writes the account's interaction
// records without prompt or output text and skips guardrail blocked-text
// capture for it. Usage, cost and token counts are unaffected. Records written
// before the switch was turned on are not rewritten.

export interface AccountContentRetention {
  accountId: string;
  zeroContentRetention: boolean;
}

export async function getAccountContentRetention(
  accountId: string
): Promise<AccountContentRetention> {
  const { data } = await parleyHttpClient.get<AccountContentRetention>(
    `/v1/admin/accounts/${accountId}/content-retention`
  );
  return data;
}

export async function setAccountContentRetention(
  accountId: string,
  zeroContentRetention: boolean
): Promise<AccountContentRetention> {
  const { data } = await parleyHttpClient.put<AccountContentRetention>(
    `/v1/admin/accounts/${accountId}/content-retention`,
    { zeroContentRetention }
  );
  return data;
}

// ── Rate-limit overrides (parley-api ADR-040) ──
// Super-admin only. The account override replaces the stage default; a
// per-key override gives one key its own buckets, enforced in addition to
// the account buckets (which always cap the account's aggregate). null
// clears an override. Both PUTs are a full replace of {rpmLimit, tpmLimit}.

export interface ApiKeyRateLimit {
  hashedApiKey: string | null;
  label: string;
  keyHint: string | null;
  krbName: string;
  rpmLimit: number | null;
  tpmLimit: number | null;
}

export interface AccountRateLimit {
  accountId: string;
  rpmLimit: number | null;
  tpmLimit: number | null;
  effectiveRpmLimit: number;
  effectiveTpmLimit: number;
  defaultRpmLimit: number;
  defaultTpmLimit: number;
  keys: ApiKeyRateLimit[];
}

export interface RateLimitUpdate {
  rpmLimit: number | null;
  tpmLimit: number | null;
}

export async function getAccountRateLimit(accountId: string): Promise<AccountRateLimit> {
  const { data } = await parleyHttpClient.get<AccountRateLimit>(
    `/v1/admin/accounts/${accountId}/rate-limit`
  );
  return data;
}

export async function setAccountRateLimit(
  accountId: string,
  update: RateLimitUpdate
): Promise<{
  accountId: string;
  rpmLimit: number | null;
  tpmLimit: number | null;
  effectiveRpmLimit: number;
  effectiveTpmLimit: number;
}> {
  const { data } = await parleyHttpClient.put(`/v1/admin/accounts/${accountId}/rate-limit`, update);
  return data;
}

export async function setApiKeyRateLimit(
  accountId: string,
  hashedApiKey: string,
  update: RateLimitUpdate
): Promise<{
  accountId: string;
  hashedApiKey: string;
  rpmLimit: number | null;
  tpmLimit: number | null;
}> {
  const { data } = await parleyHttpClient.put(
    `/v1/admin/accounts/${accountId}/keys/${hashedApiKey}/rate-limit`,
    update
  );
  return data;
}

// ── Credit ledger ──

// Paginated, newest-first, append-only credit ledger for an account.
export async function getAccountTransactions(
  accountId: string,
  params?: { page?: number; limit?: number }
): Promise<AccountTransactionsResponse> {
  const { data } = await parleyHttpClient.get<AccountTransactionsResponse>(
    `/v1/accounts/${accountId}/transactions`,
    { params }
  );
  return data;
}

// Manual admin credit / correction. Appends an immutable admin_grant ledger
// row and atomically moves creditBalance. amountUsd is signed — negative for
// a clawback. Requires admin/super_admin on the API side.
export async function postAccountTransaction(
  accountId: string,
  body: { amountUsd: number; reason: string }
): Promise<PostTransactionResponse> {
  const { data } = await parleyHttpClient.post<PostTransactionResponse>(
    `/v1/accounts/${accountId}/transactions`,
    body
  );
  return data;
}

// ── Keys ──

// `expire` maps to the API's 30/90-day expiry; 'never' (the default) omits
// the field so the key has no expiry.
export async function createKey(
  accountId: string,
  krbName: string,
  label: string,
  expire: KeyExpiryChoice = 'never',
  monthlyBudgetLimit: number,
  monthlyBudgetLimitAlertThreshold: number
): Promise<CreateKeyResponse> {
  const { data } = await parleyHttpClient.post<CreateKeyResponse>(`/v1/accounts/${accountId}/key`, {
    krbName,
    label,
    ...(expire !== 'never' ? { expire } : {}),
    monthlyBudgetLimit,
    monthlyBudgetLimitAlertThreshold,
  });
  return data;
}

// Set an explicit expiry on an existing key, or clear it. Pass an ISO 8601
// future timestamp to set a new expiry, or null so the key never expires.
export async function updateKeyExpiry(
  accountId: string,
  krbName: string,
  label: string,
  expiryDate: string | null
): Promise<CreateKeyResponse> {
  const { data } = await parleyHttpClient.patch<CreateKeyResponse>(
    `/v1/accounts/${accountId}/key/expiry`,
    { krbName, label, expiryDate }
  );
  return data;
}

export async function regenerateKey(
  accountId: string,
  krbName: string,
  label: string
): Promise<CreateKeyResponse> {
  const { data } = await parleyHttpClient.put<CreateKeyResponse>(
    `/v1/accounts/${accountId}/key/regenerate`,
    { krbName, label }
  );
  return data;
}

export async function setKeyPaused(
  accountId: string,
  hashedApiKey: string,
  paused: boolean
): Promise<{ accountId: string; hashedApiKey: string; paused: boolean }> {
  const { data } = await parleyHttpClient.patch(
    `/v1/accounts/${accountId}/keys/${hashedApiKey}/pause`,
    { paused }
  );
  return data;
}

export async function renameKey(
  accountId: string,
  hashedApiKey: string,
  label: string
): Promise<{ accountId: string; hashedApiKey: string; label: string }> {
  const { data } = await parleyHttpClient.patch(
    `/v1/accounts/${accountId}/keys/${hashedApiKey}/label`,
    { label }
  );
  return data;
}

// ── People search (typeahead for "add a key") ──

export interface PeopleSearchHit {
  krbName: string;
  displayName: string;
  mitEmail?: string;
  jobTitle?: string;
  dlcName?: string;
  profilePictureUrl?: string;
  affiliations: string[];
}

export async function searchPeople(query: string): Promise<PeopleSearchHit[]> {
  const { data } = await parleyHttpClient.get<{ people: PeopleSearchHit[] }>(
    `/v1/accounts/people-search`,
    { params: { q: query } }
  );
  return data.people ?? [];
}

// ── Moira list preview ──

export interface MoiraListPreview {
  listName: string;
  count: number;
  members: { kerb: string }[];
}

export async function getMoiraList(listName: string): Promise<MoiraListPreview> {
  const { data } = await parleyHttpClient.get<MoiraListPreview>(
    `/v1/accounts/moira-lists/${encodeURIComponent(listName)}`
  );
  return data;
}

// ── Moira list attachments ──
// Replaces the retired point-in-time bulk issuance: attaching a list mints
// no keys. Members get a key when they first sign in, and the backend's
// nightly job pauses keys for people who leave every attached list.

export interface MoiraAttachment {
  accountId: string;
  listName: string;
  attachedAt: string;
  attachedBy: string;
  defaultExpiryDays?: 30 | 90;
  keyLabelBase?: string;
  defaultBudgetLimits?: {
    monthlyBudgetLimit?: number;
    termBudgetLimit?: number;
    monthlyBudgetLimitAlertThreshold?: number;
    termBudgetLimitAlertThreshold?: number;
  };
  lastSyncAt?: string;
  lastSyncStatus?: 'ok' | 'not_found' | 'error';
  lastMemberCount?: number;
}

export interface AttachMoiraListResult {
  attachment: MoiraAttachment;
  memberCount: number;
  invitesScheduled: number;
}

export async function attachMoiraList(
  accountId: string,
  listName: string,
  monthlyBudgetLimit: number,
  monthlyBudgetLimitAlertThreshold: number,
  keyLabelBase?: string,
  expire: KeyExpiryChoice = 'never'
): Promise<AttachMoiraListResult> {
  const { data } = await parleyHttpClient.post<AttachMoiraListResult>(
    `/v1/accounts/${accountId}/moira-attachments`,
    {
      listName,
      ...(keyLabelBase ? { keyLabelBase } : {}),
      ...(expire !== 'never' ? { expire } : {}),
      monthlyBudgetLimit,
      monthlyBudgetLimitAlertThreshold,
    }
  );
  return data;
}

export async function getMoiraAttachments(accountId: string): Promise<MoiraAttachment[]> {
  const { data } = await parleyHttpClient.get<{ attachments: MoiraAttachment[] }>(
    `/v1/accounts/${accountId}/moira-attachments`
  );
  return data.attachments ?? [];
}

export async function detachMoiraList(
  accountId: string,
  listName: string
): Promise<{ detached: boolean; keysPaused: number }> {
  const { data } = await parleyHttpClient.delete<{ detached: boolean; keysPaused: number }>(
    `/v1/accounts/${accountId}/moira-attachments/${encodeURIComponent(listName)}`
  );
  return data;
}

// Whether the Moira attachment feature is enabled (super-admin toggled).
// Drives whether the add-key dialog offers the "Moira list" mode.
export async function getMoiraAttachmentsEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    `/v1/admin/config/moira-attachments`
  );
  return data.enabled === true;
}

// ── Usage ──

export async function getAccountUsage(
  accountId: string,
  params: UsageQueryParams
): Promise<UsageResponse> {
  const { data } = await parleyHttpClient.get<UsageResponse>(`/v1/accounts/${accountId}/usage`, {
    params,
  });
  return data;
}

export async function getAccountBudgetLimits(
  accountId: string
): Promise<AccountBudgetLimitDetails> {
  const { data } = await parleyHttpClient.get<AccountBudgetLimitDetails>(
    `/v1/accounts/${accountId}/budget`
  );
  return data;
}

export async function getAccountUsageByKey(
  accountId: string,
  params: { from?: string; to: string; range?: 'all'; cache?: 'bypass' }
): Promise<PerKeyUsageBreakdown> {
  const { data } = await parleyHttpClient.get<PerKeyUsageBreakdown>(
    `/v1/accounts/${accountId}/usage/by-key`,
    { params }
  );
  return data;
}

export async function getKeyUsage(
  accountId: string,
  krbName: string,
  params: UsageQueryParams & { hashedApiKey?: string; keyHint?: string; label?: string }
): Promise<UsageResponse> {
  const { data } = await parleyHttpClient.get<UsageResponse>(
    `/v1/accounts/${accountId}/keys/${encodeURIComponent(krbName)}/usage`,
    { params }
  );
  return data;
}

export async function exportAccountUsage(
  accountId: string,
  params: UsageExportParams
): Promise<Blob> {
  const { data } = await parleyHttpClient.get(`/v1/accounts/${accountId}/usage/export`, {
    params,
    responseType: 'blob',
  });
  return data as Blob;
}

export async function exportAllUsage(params: UsageExportParams): Promise<Blob> {
  const { data } = await parleyHttpClient.get('/v1/admin/usage/export', {
    params,
    responseType: 'blob',
  });
  return data as Blob;
}

// ── Bulk Operations ──

export async function bulkCreateAccounts(
  request: BulkCreateAccountRequest
): Promise<BulkCreateAccountsResponse> {
  const { data } = await parleyHttpClient.post<BulkCreateAccountsResponse>(
    '/v1/accounts/bulk',
    request
  );
  return data;
}

export async function getBulkJobStatus(jobId: string): Promise<BulkOperationStatusResponse> {
  const { data } = await parleyHttpClient.get<BulkOperationStatusResponse>(
    `/v1/accounts/bulk/${jobId}`
  );
  return data;
}

export async function getBulkJobResults(jobId: string): Promise<BulkOperationResultsResponse> {
  const { data } = await parleyHttpClient.get<BulkOperationResultsResponse>(
    `/v1/accounts/bulk/${jobId}/results`
  );
  return data;
}

// ── Admin Dashboard ──

// Range param: pass `{ from, to }` for a rolling window, or `{ to, range: 'all' }`
// to let the API resolve the lower bound server-side. See useRange composable.
export interface DashboardRangeParams {
  from?: string;
  to: string;
  range?: 'all';
}

export async function getAdminDashboardStats(
  params: DashboardRangeParams
): Promise<AdminDashboardStats> {
  // Platform-wide stats aggregate every raw USAGE record in the window on a
  // cache miss (~35s on a 3-week prod range), which exceeds the shared
  // client's 30s timeout. Disable the client timeout and let the backend
  // govern (timeoutMiddleware ~13 min / ALB idle 10 min).
  const { data } = await parleyHttpClient.get<AdminDashboardStats>('/v1/admin/dashboard/stats', {
    params,
    timeout: 0,
  });
  return data;
}

export async function getMyDashboardStats(
  params: DashboardRangeParams,
  options: { bypassCache?: boolean } = {}
): Promise<AdminDashboardStats> {
  const merged: Record<string, string | undefined> = { ...params, scope: 'mine' };
  if (options.bypassCache) merged.cache = 'bypass';
  const { data } = await parleyHttpClient.get<AdminDashboardStats>('/v1/accounts/dashboard/stats', {
    params: merged,
  });
  return data;
}

export async function getAccountDashboardStats(
  accountId: string,
  params: DashboardRangeParams
): Promise<AdminDashboardStats> {
  const { data } = await parleyHttpClient.get<AdminDashboardStats>('/v1/accounts/dashboard/stats', {
    params: { ...params, accountId },
  });
  return data;
}

// ── Parley CLI ──

// Fleet-wide Parley CLI adoption for the Configuration → Parley CLI page.
// super_admin / support only (backend: denyIfNotPlatformStaff).
export async function getCliInstallStats(
  params?: CliInstallStatsParams
): Promise<CliInstallStatsResponse> {
  const { data } = await parleyHttpClient.get<CliInstallStatsResponse>('/v1/admin/cli/stats', {
    params,
  });
  return data;
}

// One account's CLI footprint — the installs and people behind its `usesCli`
// flag. Returns usesCli:false with an empty installs[] when the account has
// never been used from the CLI. super_admin / support only.
export async function getAccountCliUsage(accountId: string): Promise<AccountCliUsageDetail> {
  const { data } = await parleyHttpClient.get<AccountCliUsageDetail>(
    `/v1/admin/cli/accounts/${accountId}`
  );
  return data;
}

// ── Stripe ──
//
// Companion to the funding-source enum on Account. The whole flow lives
// under /v1/accounts/:id/stripe/* — see docs/architecture/06-account-model.md §3
// and the route handlers in parley-api/api/account/stripe/index.ts.

export interface StripeSetupIntentResponse {
  clientSecret: string;
  customerId: string;
}

export interface StripeAttachResponse {
  fundingSource: 'stripe';
  billingMode: 'postpaid';
  stripeBillingStatus: 'active';
  card: StripePaymentMethod['card'];
}

export async function createStripeSetupIntent(
  accountId: string
): Promise<StripeSetupIntentResponse> {
  const { data } = await parleyHttpClient.post<StripeSetupIntentResponse>(
    `/v1/accounts/${accountId}/stripe/setup-intent`
  );
  return data;
}

export async function attachStripePaymentMethod(
  accountId: string,
  paymentMethodId: string
): Promise<StripeAttachResponse> {
  const { data } = await parleyHttpClient.post<StripeAttachResponse>(
    `/v1/accounts/${accountId}/stripe/payment-method`,
    { paymentMethodId }
  );
  return data;
}

export async function getStripePaymentMethod(accountId: string): Promise<StripePaymentMethod> {
  const { data } = await parleyHttpClient.get<StripePaymentMethod>(
    `/v1/accounts/${accountId}/stripe/payment-method`
  );
  return data;
}

export async function detachStripePaymentMethod(
  accountId: string
): Promise<{ fundingSource: 'none'; billingMode: 'prepaid' }> {
  const { data } = await parleyHttpClient.delete<{ fundingSource: 'none'; billingMode: 'prepaid' }>(
    `/v1/accounts/${accountId}/stripe/payment-method`
  );
  return data;
}

// ── Public Stripe config (publishable key) ──

export interface StripeConfigResponse {
  publishableKey: string;
}

export async function getStripeConfig(): Promise<StripeConfigResponse> {
  const { data } = await parleyHttpClient.get<StripeConfigResponse>('/v1/config/stripe');
  return data;
}

// ── Admin config: ServiceNow reporting feed flag ──
// Whether the monthly billing run submits one charge record per billed
// account to the ServiceNow reporting intake (parley-api ADR-028). The flag
// only gates submission — billing and settlement are identical either way.

export async function getServiceNowFeedEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    '/v1/admin/config/servicenow-feed'
  );
  return data.enabled;
}

export async function setServiceNowFeedEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/servicenow-feed',
    { enabled }
  );
  return data.enabled;
}

// ── Admin config: MCP gateway per-caller billing flag ──
// Whether the AgentCore gateway bills each MCP caller to its own designated
// Parley API key instead of one stage-wide shared key. Off is the safe state:
// the token-exchange endpoint refuses outright rather than falling back to the
// shared key, and turning it off also stops capabilities already in flight.

export async function getGatewayPerCallerBillingEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    '/v1/admin/config/gateway-per-caller-billing'
  );
  return data.enabled;
}

export async function setGatewayPerCallerBillingEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/gateway-per-caller-billing',
    { enabled }
  );
  return data.enabled;
}

// ── Admin config: student cost object billing flag ──
// Whether non-staff/non-gradStudent accounts may self-request an account and
// choose cost object as their billing method (parley-api). Off: self-service
// creation is limited to staff/gradStudent affiliations, and cost object is
// Stripe-only (locked) for every other account type.

export async function getStudentCostObjectBillingEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    '/v1/admin/config/student-cost-object-billing'
  );
  return data.enabled;
}

export async function setStudentCostObjectBillingEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/student-cost-object-billing',
    { enabled }
  );
  return data.enabled;
}

// ── Admin config: chargeback settlement accounting ──
// The Parley-side credit line used for cost-object settlement journal
// vouchers. The API snapshots this pair when a billing run starts, so an edit
// applies to future runs without changing one already in progress.

export interface ChargebackSettlementConfig {
  costObject: string;
  glAccount: string;
  // SAP user each settlement journal voucher posts as (X-Referred-User on the
  // Finance API call). Parley's own operational id; SAP authorizes against it.
  postingUser: string;
}

export async function getChargebackSettlementConfig(): Promise<ChargebackSettlementConfig> {
  const { data } = await parleyHttpClient.get<ChargebackSettlementConfig>(
    '/v1/admin/config/chargeback-settlement'
  );
  return data;
}

export async function setChargebackSettlementConfig(
  config: ChargebackSettlementConfig
): Promise<ChargebackSettlementConfig> {
  const { data } = await parleyHttpClient.put<ChargebackSettlementConfig>(
    '/v1/admin/config/chargeback-settlement',
    config
  );
  return data;
}

// ── Admin config: knowledge base flag ──
// Whether the "Knowledge Base" tab is visible on the account detail pages —
// the tab button and its route, on both the staff-facing (/accounts/:id/rag)
// and owner-facing (/my-accounts/:id/rag) pages. A global kill-switch that
// sits above the per-account RagConfig enablement inside the tab: off hides
// the tab for every account regardless of per-account config; on restores it,
// and per-account enablement still governs what renders inside. Off does not
// delete per-account KB config or uploaded documents.
//
// The GET is deliberately readable by any authenticated user, not just staff,
// because the owner-facing tab is drawn for account owners and account admins
// who hold no platform-staff role and still need to resolve the flag.

// ── Platform LLM operations ──
// Which model Parley uses, and which account is charged, when it calls a model
// on its own behalf. Super-admin only, since it names a spending account.
//
// The GET resolves the stored key to its account server-side, so this is the
// one place the console can truthfully say who pays. The PUT is a partial
// merge: omitted fields keep their stored value, which is what lets a tier be
// repointed or one operation switched off without re-entering the credential.

export async function getPlatformLlmConfig(): Promise<PlatformLlmConfigView> {
  const { data } = await parleyHttpClient.get<PlatformLlmConfigView>(
    '/v1/admin/config/platform-llm'
  );
  return data;
}

export async function setPlatformLlmConfig(
  body: PlatformLlmConfigInput
): Promise<PlatformLlmConfigView> {
  const { data } = await parleyHttpClient.put<PlatformLlmConfigView>(
    '/v1/admin/config/platform-llm',
    body
  );
  return data;
}

export async function getKnowledgeBaseEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    '/v1/admin/config/knowledge-base'
  );
  return data.enabled;
}

export async function setKnowledgeBaseEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/knowledge-base',
    { enabled }
  );
  return data.enabled;
}

// ── Admin config: knowledge base billing flag ──
// One switch over the CloudWatch storage reads, the daily per-account storage
// charge and retrieval billing. Off stops only the metering: knowledge bases
// keep working, since the feature itself is gated by the switch above.

export async function getKbBillingEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>('/v1/admin/config/kb-billing');
  return data.enabled;
}

export async function setKbBillingEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>('/v1/admin/config/kb-billing', {
    enabled,
  });
  return data.enabled;
}

// ── Admin config: which knowledge base source types are on offer ──
//
// One request covers all six, which is why the Config card shares a single
// in-flight promise across its rows rather than asking once per switch.

export async function getKbSourceTypes(): Promise<RagSourceTypeConfig> {
  const { data } = await parleyHttpClient.get<RagSourceTypeConfig>(
    '/v1/admin/config/kb-source-types'
  );
  return data;
}

export async function setKbSourceTypes(config: RagSourceTypeConfig): Promise<RagSourceTypeConfig> {
  const { data } = await parleyHttpClient.put<RagSourceTypeConfig>(
    '/v1/admin/config/kb-source-types',
    config
  );
  return data;
}

// ── Admin config: RAG retrieval kill switches ──
// Whether a query actually retrieves from the shared or account tier at all —
// deliberately separate from getKnowledgeBaseEnabled above, which only gates
// the KB tab's visibility. Off here means no retrieval call ever runs for
// that tier, tab visible or not. Stored as one object server-side (no partial
// update), so a single toggle flip must resend both fields.

export interface RagRetrievalConfig {
  sharedEnabled: boolean;
  accountEnabled: boolean;
}

export async function getRagRetrievalConfig(): Promise<RagRetrievalConfig> {
  const { data } = await parleyHttpClient.get<RagRetrievalConfig>('/v1/admin/config/rag-retrieval');
  return data;
}

export async function setRagRetrievalConfig(
  config: RagRetrievalConfig
): Promise<RagRetrievalConfig> {
  const { data } = await parleyHttpClient.put<RagRetrievalConfig>(
    '/v1/admin/config/rag-retrieval',
    config
  );
  return data;
}

// ── Admin config: Chat integration ──
// Whether the Chat (LibreChat) balance-lookup feature is reachable at all —
// the "Chat" sidebar item, its route, and the /v1/admin/chat/balance and
// /v1/admin/chat/transactions endpoints those pages call. Namespaced per
// feature key on one ChatConfig object (currently just `balanceCheck`); a
// future Chat feature would be a new key here, not a new endpoint. Off by
// default — a super_admin must explicitly enable it. GET is readable by both
// support and super_admin, matching read parity with the balance/
// transactions lookup endpoints; only PUT is super_admin-only.

export async function getChatConfig(): Promise<ChatConfig> {
  const { data } = await parleyHttpClient.get<ChatConfig>('/v1/admin/chat/config');
  return data;
}

export async function updateChatConfig(config: ChatConfig): Promise<ChatConfig> {
  const { data } = await parleyHttpClient.put<ChatConfig>('/v1/admin/chat/config', config);
  return data;
}

export async function getChatBalanceCheckEnabled(): Promise<boolean> {
  const config = await getChatConfig();
  return config.balanceCheck?.enabled ?? false;
}

export async function setChatBalanceCheckEnabled(enabled: boolean): Promise<boolean> {
  const config = await updateChatConfig({ balanceCheck: { enabled } });
  return config.balanceCheck?.enabled ?? false;
}

// ── Admin config: Moira allowed users list ──
// Controls which Moira list gates initial-login account creation. The list
// name is stored in SSM; changing it takes effect immediately (Redis cache
// is busted on write).

export async function getMoiraAllowedList(): Promise<string | null> {
  const { data } = await parleyHttpClient.get<{ listName: string | null }>(
    '/v1/admin/config/moira-allowed-list'
  );
  return data.listName;
}

export async function setMoiraAllowedList(listName: string): Promise<string> {
  const { data } = await parleyHttpClient.put<{ listName: string }>(
    '/v1/admin/config/moira-allowed-list',
    { listName }
  );
  return data.listName;
}

export interface MoiraMember {
  member_type: string;
  member_name: string;
}

export async function getMoiraAllowedListMembers(): Promise<{
  listName: string;
  members: MoiraMember[];
}> {
  const { data } = await parleyHttpClient.get<{ listName: string; members: MoiraMember[] }>(
    '/v1/admin/config/moira-allowed-list/members'
  );
  return data;
}

// ── Admin config: department exclusion list ──
// Departments (LDAP `department` attribute, matched case-insensitively)
// whose users are skipped when auto-creating a default personal account on
// initial login. Stored in SSM; writes bust the Redis cache immediately.

export async function getDepartmentExclusions(): Promise<string[]> {
  const { data } = await parleyHttpClient.get<{ departments: string[] }>(
    '/v1/admin/config/department-exclusions'
  );
  return data.departments;
}

export async function addDepartmentExclusion(department: string): Promise<string[]> {
  const { data } = await parleyHttpClient.post<{ departments: string[] }>(
    '/v1/admin/config/department-exclusions',
    { department }
  );
  return data.departments;
}

export async function removeDepartmentExclusion(department: string): Promise<string[]> {
  const { data } = await parleyHttpClient.delete<{ departments: string[] }>(
    '/v1/admin/config/department-exclusions',
    { data: { department } }
  );
  return data.departments;
}

// ── Admin config: Moira attachment flags ──
// Two super-admin switches for the Moira list attachment feature (parley-api
// ADR-033): the feature flag itself (attach routes, lazy key mint on sign-in,
// nightly membership reconcile) and enforce mode for the nightly reconcile
// job. Enforce off = the job runs report-only. The feature-flag getter lives
// with the account helpers above (getMoiraAttachmentsEnabled).

export async function setMoiraAttachmentsEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/moira-attachments',
    { enabled }
  );
  return data.enabled;
}

export async function getMoiraReconcileEnforceEnabled(): Promise<boolean> {
  const { data } = await parleyHttpClient.get<{ enabled: boolean }>(
    '/v1/admin/config/moira-reconcile-enforce'
  );
  return data.enabled;
}

export async function setMoiraReconcileEnforceEnabled(enabled: boolean): Promise<boolean> {
  const { data } = await parleyHttpClient.put<{ enabled: boolean }>(
    '/v1/admin/config/moira-reconcile-enforce',
    { enabled }
  );
  return data.enabled;
}

// ── Admin config: Bedrock guardrails ──
// Global guardrail config for chat traffic (parley-api ADR-035). id/version
// identify the CDK-provisioned Bedrock guardrail; `enabled` switches
// evaluation on or off gateway-wide. Stored in SSM; writes bust the Redis
// cache immediately. Per-account overrides are a separate account-level call.

export interface GuardrailConfig {
  guardrailId: string;
  guardrailVersion: string;
  enabled: boolean;
  // ADR-038: whether a blocked event also stores the text that was judged.
  // Absent reads as off on the API side too, so leaving it undefined is safe.
  captureBlockedText?: boolean;
}

export async function getGuardrailConfig(): Promise<GuardrailConfig> {
  const { data } = await parleyHttpClient.get<GuardrailConfig>('/v1/admin/guardrails/config');
  return data;
}

export async function setGuardrailConfig(config: GuardrailConfig): Promise<GuardrailConfig> {
  const { data } = await parleyHttpClient.put<GuardrailConfig>(
    '/v1/admin/guardrails/config',
    config
  );
  return data;
}

// ── Admin config: guardrail content policy (ADR-038) ──
// The filter matrix is admin-managed at runtime rather than defined in CDK.
// A save writes the guardrail's DRAFT, mints an immutable version, waits for it
// to become usable, and only then repoints the config at it, so it is slow by
// API standards (several seconds) and worth a loading state.

export const GUARDRAIL_FILTER_TYPES = [
  'HATE',
  'INSULTS',
  'SEXUAL',
  'VIOLENCE',
  'MISCONDUCT',
  'PROMPT_ATTACK',
] as const;
export type GuardrailFilterType = (typeof GUARDRAIL_FILTER_TYPES)[number];

export const GUARDRAIL_FILTER_STRENGTHS = ['NONE', 'LOW', 'MEDIUM', 'HIGH'] as const;
export type GuardrailFilterStrength = (typeof GUARDRAIL_FILTER_STRENGTHS)[number];

export interface GuardrailContentFilter {
  type: GuardrailFilterType;
  inputStrength: GuardrailFilterStrength;
  outputStrength: GuardrailFilterStrength;
}

export interface GuardrailPolicy {
  filters: GuardrailContentFilter[];
  activeVersion: string;
  updatedAt?: string;
}

export interface GuardrailVersionSummary {
  version: string;
  description?: string;
  status?: string;
  createdAt?: string;
  active: boolean;
}

export async function getGuardrailPolicy(): Promise<GuardrailPolicy> {
  const { data } = await parleyHttpClient.get<GuardrailPolicy>('/v1/admin/guardrails/policy');
  return data;
}

export async function setGuardrailPolicy(
  filters: GuardrailContentFilter[]
): Promise<GuardrailPolicy> {
  const { data } = await parleyHttpClient.put<GuardrailPolicy>('/v1/admin/guardrails/policy', {
    filters,
  });
  return data;
}

export async function listGuardrailPolicyVersions(): Promise<GuardrailVersionSummary[]> {
  const { data } = await parleyHttpClient.get<{ versions: GuardrailVersionSummary[] }>(
    '/v1/admin/guardrails/policy/versions'
  );
  return data.versions;
}

// ── Admin: guardrail events ──

export interface GuardrailAssessmentEntry {
  policy: 'content' | 'topic' | 'word' | 'pii';
  name: string;
  detected: boolean;
  confidence?: string;
  filterStrength?: string;
  action?: string;
}

export interface GuardrailEvent {
  accountId: string;
  requestId: string;
  apiKeyHash: string;
  provider: string;
  model: string;
  source: 'INPUT' | 'OUTPUT';
  stream: boolean;
  blocked: boolean;
  evaluationError: boolean;
  categories?: string[];
  refusalText?: string;
  evaluatedChars: number;
  createdAt: string;
  // Opaque handle for the detail fetch.
  eventId: string;
  assessments?: GuardrailAssessmentEntry[];
  // List rows report whether text was captured; only the detail route returns it.
  hasEvaluatedText?: boolean;
  evaluatedText?: string;
  evaluatedTextTruncated?: boolean;
}

export interface GuardrailEventPage {
  events: GuardrailEvent[];
  // Absent once the range is exhausted. Pass it back as `cursor` for the next page.
  cursor?: string;
  // 'interventions' is the cross-account blocks-only feed; 'account' is the
  // per-account view, which also shows clean passes and evaluation errors.
  scope: 'interventions' | 'account';
  from: string;
  to: string;
}

export const listGuardrailEvents = async (params?: {
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
  accountId?: string;
}): Promise<GuardrailEventPage> => {
  const { data } = await parleyHttpClient.get<GuardrailEventPage>('/v1/admin/guardrails/events', {
    params,
  });
  return data;
};

export const getGuardrailEvent = async (
  accountId: string,
  eventId: string
): Promise<GuardrailEvent> => {
  const { data } = await parleyHttpClient.get<GuardrailEvent>(
    `/v1/admin/guardrails/events/${encodeURIComponent(accountId)}/${encodeURIComponent(eventId)}`
  );
  return data;
};

// ── Admin: scheduled jobs ──

export type JobRunStatus = 'success' | 'partial' | 'failed' | 'skipped';
export type JobRunTrigger = 'schedule' | 'manual' | 'api';
export type JobRunDetail = Record<string, string | number | boolean>;

export interface JobRunStep {
  name: string;
  status?: JobRunStatus;
  succeeded: number;
  failed: number;
  skipped?: number;
  durationMs?: number;
  detail?: JobRunDetail;
  error?: string;
}

export interface JobRunError {
  message: string;
  name?: string;
  step?: string;
  entityId?: string;
  stack?: string;
}

export interface JobRun {
  runId: string;
  jobName: string;
  period: string;
  status: JobRunStatus;
  trigger?: JobRunTrigger;
  ranAt: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  steps: JobRunStep[];
  summary?: JobRunDetail;
  errors?: JobRunError[];
  errorsOmitted?: number;
  context?: JobRunDetail;
  exitCode?: number;
}

export interface JobDefinition {
  name: string;
  label: string;
  description: string;
  cadence: string;
  retentionDays?: number;
}

export interface JobSummary extends JobDefinition {
  lastRun: {
    runId: string;
    status: JobRunStatus;
    ranAt: string;
    durationMs?: number;
    period: string;
    errorCount: number;
  } | null;
}

export interface JobRunPage {
  runs: JobRun[];
  cursor?: string;
}

export const listJobs = async (): Promise<JobSummary[]> => {
  const { data } = await parleyHttpClient.get<{ jobs: JobSummary[] }>('/v1/admin/jobs');
  return data.jobs;
};

export const listAllJobRuns = async (params?: {
  limit?: number;
  cursor?: string;
}): Promise<JobRunPage> => {
  const { data } = await parleyHttpClient.get<JobRunPage>('/v1/admin/jobs/runs', { params });
  return data;
};

export const listJobRuns = async (
  jobName: string,
  params?: { limit?: number; cursor?: string; status?: JobRunStatus }
): Promise<JobRunPage & { job: JobDefinition | null }> => {
  const { data } = await parleyHttpClient.get<JobRunPage & { job: JobDefinition | null }>(
    `/v1/admin/jobs/${encodeURIComponent(jobName)}/runs`,
    { params }
  );
  return data;
};

export const getJobRun = async (
  jobName: string,
  runId: string
): Promise<{ run: JobRun; job: JobDefinition | null }> => {
  const { data } = await parleyHttpClient.get<{ run: JobRun; job: JobDefinition | null }>(
    `/v1/admin/jobs/${encodeURIComponent(jobName)}/runs/${encodeURIComponent(runId)}`
  );
  return data;
};

// ── Models ──

export async function clearModelsRedisCache(): Promise<
  AxiosResponse<{ message: string; deleted: number[] }>
> {
  return parleyHttpClient.post<{ message: string; deleted: number[] }>(
    `/v1/admin/cache/models/bust`
  );
}

// Drops the cached Moira group and LDAP profile lookups for one person (by MIT
// ID), or for everyone when mitId is omitted, so a membership change applies
// now instead of after the 5-minute TTL. super_admin only.
export async function clearAuthCache(
  mitId?: string
): Promise<{ message: string; mitId: string | null; deleted: number }> {
  const { data } = await parleyHttpClient.post<{
    message: string;
    mitId: string | null;
    deleted: number;
  }>('/v1/admin/cache/auth/bust', mitId ? { mitId } : {});
  return data;
}

// ── Notifications ──

//TODO make not specific to cost objects

export async function getCostObjectApprovals(
  status?: string
): Promise<AxiosResponse<{ requests: CostObjectApprovalRecord[] }>> {
  const config = status ? { params: { status } } : {};
  return parleyHttpClient.get<{ requests: CostObjectApprovalRecord[] }>(
    `/v1/notifications/cost-object-requests`,
    config
  );
}

export async function getCostObjectApprovalPendingBySupervisor(): Promise<
  AxiosResponse<{ requests: CostObjectApprovalRecord[] }>
> {
  return parleyHttpClient.get<{ requests: CostObjectApprovalRecord[] }>(
    `/v1/notifications/supervisor/cost-object-requests`
  );
}

export async function getDormantKeyNotifications(): Promise<
  AxiosResponse<{ notifications: DormantKeyNotification[] }>
> {
  return parleyHttpClient.get<{ notifications: DormantKeyNotification[] }>(
    `/v1/notifications/account-admins/dormant-key-pauses`
  );
}

export async function getCostObjectApprovalPendingByUser(
  email?: string
): Promise<AxiosResponse<{ requests: CostObjectApprovalRecord[] }>> {
  const config = email ? { params: { email } } : {};

  return parleyHttpClient.get<{ requests: CostObjectApprovalRecord[] }>(
    `/v1/notifications/user/cost-object-requests`,
    config
  );
}

// Cost-object-request record for one account's current cost object, if any.
// Backed by a direct DynamoDB GetItem (not the GSI1 "pending" index the
// functions above use), so unlike them this is safe to call immediately
// after a mutation without racing GSI replication lag.
export async function getCostObjectRequestForAccount(
  accountId: string
): Promise<AxiosResponse<{ requests: CostObjectApprovalRecord[] }>> {
  return parleyHttpClient.get<{ requests: CostObjectApprovalRecord[] }>(
    `/v1/notifications/account/${accountId}/cost-object-request`
  );
}

// Loads a single approval request's detail from the API. The approve page is
// reached from an email link that carries only the accountId in the path; this
// hydrates everything the page renders so no requester/cost-object data lives in
// the URL.
export async function getCostObjectApprovalDetail(
  accountId: string
): Promise<CostObjectApprovalDetail> {
  const { data } = await parleyHttpClient.get<{ request: CostObjectApprovalDetail }>(
    `/v1/notifications/cost-object-request/${accountId}`
  );
  return data.request;
}

// ── Health ──

export async function ping(): Promise<HealthResponse> {
  const { data } = await parleyHttpClient.get<HealthResponse>('/api-health/ping');
  return data;
}

export async function healthStatus(): Promise<HealthResponse> {
  const { data } = await parleyHttpClient.get<HealthResponse>('/api-health/status');
  return data;
}
